import { useEffect, useRef, useState } from 'react';
import { loadingScheduler } from '../services/loading-scheduler';
import { chatEventEmitter } from '@wix/chat-sdk';
import { useCorvidApi } from './corvid-api';
import { useAppState } from './app-state';
import { CHAT_DEF_ID } from '../constants';

export const useInitChatSdk = ({ coreChatToken, experiments, instance }) => {
  const [isSdkLoaded, setSdkLoaded] = useState(false);
  const isSdkInitialized = useRef(false);
  const { appState } = useAppState();
  const corvidApi = useCorvidApi();

  useEffect(() => {
    const onNewRealtimeMessage = (message) =>
      corvidApi.reportMessageReceived(message);

    const initChatSdk = () => {
      void loadingScheduler.onInteractive().then(async () => {
        if (!coreChatToken) {
          return;
        }
        const linguistHeader = [
          appState.language,
          appState.locale,
          appState.isPrimaryLanguage,
          appState.instanceId,
        ].join('|');

        const chatSdk = await import(
          /* webpackChunkName: "chat-sdk" */ '@wix/chat-sdk'
        );

        const isPresenceOverDuplexerEnabled = experiments
          ? experiments['specs.PresenceOverDuplexerSpec'] === 'true'
          : false;

        const tokenServer = instance // TODO: Hack to bypass testkit issues
          ? { url: '/_api', authorization: instance, isForUnifiedList: false }
          : undefined;

        if (isSdkInitialized.current && tokenServer) {
          // `init` is a no-op once the sdk is initialized (specs.inbox.DontReinitChatSDK),
          // so a chatToken issued after a visitor merge has to be applied through
          // `reconnect`. Without it the sdk keeps the visitor's token while the widget
          // moves to the merged contact chatroom, and every read of that chatroom
          // answers PARTICIPANT_NOT_JOINED_CHATROOM.
          await chatSdk.chatSdk
            .reconnect({ chatToken: coreChatToken, tokenServer })
            .catch((e) => console.error('ErrorOnReconnectChatSdk', e));
        } else {
          chatSdk.chatSdk.init({
            chatServerUrl: location.origin,
            chatToken: coreChatToken,
            preFetch: false,
            experiments,
            httpHeaders: {
              'x-wix-linguist': linguistHeader,
            },
            tokenServer,
            presenceOptions: {
              useNewConvention: false,
              disableWatch: isPresenceOverDuplexerEnabled,
            },
            appId: CHAT_DEF_ID,
          });
          isSdkInitialized.current = true;
        }

        chatEventEmitter.addListener(
          chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
          onNewRealtimeMessage,
        );

        setSdkLoaded(true);
        loadingScheduler.triggerConnectivity();
      });
    };

    const disconnectChatSdk = () => {
      void loadingScheduler.onInteractive().then(async () => {
        if (!coreChatToken) {
          return;
        }

        chatEventEmitter.removeListener(
          chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
          onNewRealtimeMessage,
        );

        const chatSdk = await import(
          /* webpackChunkName: "chat-sdk" */ '@wix/chat-sdk'
        );
        chatSdk.chatSdk.disconnect();

        setSdkLoaded(false);
      });
    };

    const isBfCacheFixEnabled = experiments
      ? experiments['specs.chat.BfCacheFix'] === 'true'
      : false;

    const handlePageHide = () => disconnectChatSdk();
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // coming from bfcache
        initChatSdk();
      }
    };

    if (isBfCacheFixEnabled) {
      window.addEventListener('pagehide', handlePageHide);
      window.addEventListener('pageshow', handlePageShow);
    }

    initChatSdk();

    return () => {
      disconnectChatSdk();
      if (isBfCacheFixEnabled) {
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('pageshow', handlePageShow);
      }
    };
  }, [loadingScheduler, coreChatToken, experiments, instance]);

  return isSdkLoaded;
};
